import { forwardRef, useState } from 'react';
import { tv } from 'tailwind-variants';

import { Icon, Menu, NavLink } from '@frontendmentorio/design-system-v2';

const iconStyles = tv({
  base: ['text-neutral-900', 'transition-transform'],
  variants: {
    isActive: {
      true: ['rotate-90'],
    },
  },
});

const MenuButton = forwardRef(({ isActive, ...props }, ref) => {
  return (
    <button {...props} aria-label="Main menu" ref={ref}>
      <Menu.Burger isActive={isActive} />
    </button>
  );
});

const HeaderButton = ({ children, isActive, ...props }) => (
  <NavLink
    className="border-b border-neutral-200 justify-between px-5 py-4 w-full tablet:px-4 tablet:py-3"
    isUnderlineOnHover={false}
    data-no-close-menu
    {...props}
  >
    {children}
    <Icon
      className={iconStyles({ isActive })}
      name="chevron-right"
      style="solid"
    />
  </NavLink>
);

export default function MainMenu() {
  const [isExploreOpen, setIsExploreOpen] = useState(true);
  const [isCompaniesOpen, setIsCompaniesOpen] = useState(false);

  return (
    <Menu
      button={MenuButton}
      className="desktop:hidden"
      dropdownClassName="right-[unset] left-0 mobile:min-w-[18rem]"
    >
      <HeaderButton
        id="explore-button"
        isActive={isExploreOpen}
        onClick={() => setIsExploreOpen(!isExploreOpen)}
      >
        Explore
      </HeaderButton>

      {isExploreOpen && (
        <Menu.Nav ariaDescribedby="explore-button">
          <Menu.Item>
            <NavLink
              className="gap-4"
              href="/learning-paths"
              isUnderlineOnHover={false}
            >
              <Icon className="text-lg" name="route" /> Learning paths
            </NavLink>
          </Menu.Item>

          <Menu.Item>
            <NavLink
              className="gap-4"
              href="/challenges"
              isUnderlineOnHover={false}
            >
              <Icon className="text-lg" name="laptop-mobile" /> Challenges
            </NavLink>
          </Menu.Item>

          <Menu.Item>
            <NavLink
              className="gap-4"
              href="/solutions"
              isUnderlineOnHover={false}
            >
              <Icon className="text-lg" name="code" /> Solutions
            </NavLink>
          </Menu.Item>

          <Menu.Item className="mx-0 px-5 last:border-b-1 tablet:px-0">
            <NavLink
              className="gap-4"
              href="/articles"
              isUnderlineOnHover={false}
            >
              <Icon className="text-lg" name="newspaper" />
              Articles
            </NavLink>
          </Menu.Item>
        </Menu.Nav>
      )}

      <HeaderButton
        id="companies-button"
        isActive={isCompaniesOpen}
        onClick={() => setIsCompaniesOpen(!isCompaniesOpen)}
      >
        For companies
      </HeaderButton>

      {isCompaniesOpen && (
        <Menu.Nav ariaDescribedby={'companies-button'}>
          <Menu.Item>
            <NavLink
              className="gap-4"
              href={process.env.HIRING_PLATFORM_URL}
              isUnderlineOnHover={false}
            >
              <Icon className="text-lg" name="user-tie" />
              Hire developers
            </NavLink>
          </Menu.Item>

          <Menu.Item className="mx-0 px-5 last:border-b-1 tablet:px-0">
            <NavLink
              className="gap-4"
              href={process.env.TEAMS_PLATFORM_URL}
              isUnderlineOnHover={false}
            >
              <Icon className="text-lg" name="user-graduate" />
              Train developers
            </NavLink>
          </Menu.Item>
        </Menu.Nav>
      )}

      <Menu.Item tagName="div">
        <NavLink className="gap-4" href="/pro" isUnderlineOnHover={false}>
          <Icon className="text-lg" name="unlock" />
          <span className="flex items-center gap-2">
            Unlock{' '}
            <span className="bg-blue-500 px-1 rounded text-white tracking-wider">
              Pro
            </span>
          </span>
        </NavLink>
      </Menu.Item>
    </Menu>
  );
}
